import styled from "styled-components";

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 24px 20px 60px 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 12px 40px 12px;
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1120px;
  box-sizing: border-box;
`;

export const TopHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  background: transparent;
  padding: 0;
  margin-top: 4px;
  margin-bottom: 24px;
  border: none;
  box-shadow: none;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
    margin-top: 4px;
    margin-bottom: 18px;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

export const LogoBox = styled.div`
  height: 40px;
  display: flex;
  align-items: center;

  img {
    height: 38px;
    width: auto;
    display: block;
  }

  @media (max-width: 768px) {
    height: 32px;
    img {
      height: 30px;
    }
  }
`;

export const HeaderDivider = styled.div`
  width: 1.5px;
  height: 28px;
  background-color: #cbd5e1;

  @media (max-width: 768px) {
    height: 22px;
  }
`;

export const PageTitleText = styled.h1`
  font-family: Roboto, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: var(--fixit);
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

/* Aliases para compatibilidade */
export const ProductBanner = TopHeader;
export const ProductThumbnail = LogoBox;
export const ProductTitle = PageTitleText;

export const GreetingSection = styled.div`
  margin-bottom: 24px;
  width: 100%;

  .greeting-title {
    font-family: Roboto, sans-serif;
    font-size: 22px;
    font-weight: 400;
    color: #4a5568;
    margin: 0;
    line-height: 1.3;

    strong {
      color: #1a202c;
      font-weight: 700;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 18px;

    .greeting-title {
      font-size: 19px;
    }
  }
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  font-family: Roboto, sans-serif;
  white-space: nowrap;
  background-color: rgba(242, 153, 74, 0.12);
  color: #f2994a;
`;

export const StatusBolinha = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  display: inline-block;
  flex-shrink: 0;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* --- HORIZONTAL TABS NAVIGATION --- */

export const TabsNavWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 24px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TabsNavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: max-content;
  padding-bottom: 2px;
`;

export const TabNavItem = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? "var(--fixit)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--fixit)" : "var(--dark-gray)")};
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  margin-bottom: -1px;

  svg {
    font-size: 16px;
    color: ${({ $active }) => ($active ? "var(--fixit)" : "var(--gray)")};
    transition: color 0.2s ease;
  }

  &:hover {
    color: var(--fixit);
    background-color: rgba(152, 53, 209, 0.04);
    border-radius: 6px 6px 0 0;

    svg {
      color: var(--fixit);
    }
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
  }
`;

export const TabBadge = styled.span<{ $variant?: "default" | "success" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ $variant }) =>
    $variant === "success"
      ? "rgba(39, 174, 96, 0.12)"
      : $variant === "neutral"
      ? "rgba(0, 0, 0, 0.06)"
      : "rgba(152, 53, 209, 0.12)"};
  color: ${({ $variant }) =>
    $variant === "success"
      ? "#27ae60"
      : $variant === "neutral"
      ? "var(--gray)"
      : "var(--fixit)"};
  white-space: nowrap;
`;

export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

/* --- SECTION BLOCK (WHITE CARD PATTERN) --- */

export const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 16px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.03);
  padding: 28px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 18px 14px;
    border-radius: 12px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }
`;

export const SectionTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--dark-gray);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: var(--fixit);
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 13px;
  color: var(--gray);
  margin: 0;
  line-height: 1.4;
`;

/* --- PERSON TYPE PILL SWITCH --- */

export const PersonTypeSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

export const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 4px;
  width: fit-content;

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
      text-align: center;
      padding: 8px 12px;
    }
  }
`;

export const SwitchOption = styled.button<{ selected: boolean }>`
  border: none;
  background-color: ${({ selected }) =>
    selected ? "var(--white)" : "transparent"};
  color: ${({ selected }) =>
    selected ? "var(--fixit)" : "var(--dark-gray)"};
  font-weight: ${({ selected }) => (selected ? "600" : "400")};
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: ${({ selected }) =>
    selected ? "0px 2px 4px rgba(0, 0, 0, 0.05)" : "none"};
  transition: all 0.2s;
  font-size: 14px;
`;

/* --- DEV / UTILITY BUTTONS --- */

export const SeedFloatingButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  background-color: #ff9800;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e68a00;
    transform: translateY(-2px);
    box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const IconWrapper = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  margin-bottom: 20px;
`;

export const Title = styled.h2`
  font-size: 24px;
  color: #333;
  margin: 0 0 10px;
`;

export const Message = styled.p`
  font-size: 16px;
  color: #555;
  line-height: 1.6;
  margin: 0;
`;

export const Actions = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: center;

  & > button,
  & > a {
    min-width: 180px;
  }
`;

/* Compatibilidade com layouts legados se necessário */
export const Container = PageContainer;
export const FormContainer = ContentWrapper;
export const StepperContainer = TabsNavWrapper;
export const StepTab = TabNavItem;
export const ChangeLanguageContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
`;
export const ChangePriceContainer = styled.div`
  position: fixed;
  bottom: 60px;
  left: 20px;
  z-index: 1000;
`;
export const PriceSelect = styled.select`
  display: none;
`;
